import type { CamposDeLaFila, PlantacionDeInforme } from "@/lib/informes";
import { textoDeDecimal, textoDeNumero } from "@/lib/montos";
import { numeroEscrito } from "@/lib/numeroEscrito";

/* ---------------------------------------------------------------------------
   La pestaña "Descriptor de plantaciones": traducción formulario ↔ API,
   AGRUPACIÓN POR ESPECIE y la ayuda de escritura de ese campo.

   ES EL ÚNICO DE LOS CUATRO MÓDULOS DE PESTAÑA QUE HACE ALGO MÁS QUE
   TRADUCIR, y ahí está la razón de que viva fuera del componente. La
   agrupación es una decisión de PRESENTACIÓN que el servidor no toma (el
   stakeholder lo dijo con todas las letras: "esto solo se despliega de esa
   forma en el front… es solo UX"), así que si la lógica quedara adentro del
   árbol, lo único que un test podría ejercitar con facilidad es "dos filas de
   especies distintas" — que no distingue una agrupación correcta de una que
   parte los grupos por una mayúscula.
--------------------------------------------------------------------------- */

/**
 * Una plantación mientras se escribe.
 *
 * TODO ES STRING porque eso es lo que hay en un `<input>`: guardarlo como
 * número obligaría a decidir qué es un campo a medio teclear ("1,") y la
 * respuesta siempre sería borrar lo que la persona está escribiendo.
 *
 * NO HAY NINGÚN CAMPO DE VALOR, y esa es la diferencia de fondo con las otras
 * tres pestañas con cuadro: esto describe el huerto, no lo tasa.
 */
export interface FormularioDePlantacion {
  especie: string;
  variedad: string;
  anio_plantacion: string;
  superficie_ha: string;
  cuartel: string;
  portainjerto: string;
  distancia_plantacion: string;
  plantas_por_ha: string;
  sistema_riego: string;
  nota_terreno: string;
}

/** El formulario recién abierto. Ningún campo arranca con valor. */
export const PLANTACION_VACIA: FormularioDePlantacion = {
  especie: "",
  variedad: "",
  anio_plantacion: "",
  superficie_ha: "",
  cuartel: "",
  portainjerto: "",
  distancia_plantacion: "",
  plantas_por_ha: "",
  sistema_riego: "",
  nota_terreno: "",
};

/** Carga el formulario con una plantación ya guardada, para corregirla. */
export function formularioDesdePlantacion(
  plantacion: PlantacionDeInforme,
): FormularioDePlantacion {
  return {
    especie: plantacion.especie,
    variedad: plantacion.variedad ?? "",
    anio_plantacion: textoDeNumero(plantacion.anio_plantacion),
    // Los decimales llegan como "4.30": se muestran sin el relleno de ceros y
    // con coma. Con el punto crudo, reabrir la fila y guardarla sin tocar nada
    // cambiaría su superficie.
    superficie_ha: textoDeDecimal(plantacion.superficie_ha),
    cuartel: plantacion.cuartel ?? "",
    portainjerto: plantacion.portainjerto ?? "",
    distancia_plantacion: plantacion.distancia_plantacion ?? "",
    plantas_por_ha: textoDeNumero(plantacion.plantas_por_ha),
    sistema_riego: plantacion.sistema_riego ?? "",
    nota_terreno: plantacion.nota_terreno ?? "",
  };
}

/**
 * El cuerpo que viaja al servidor.
 *
 * Un campo vacío se manda como `null` y NO se omite: omitirlo en un PATCH
 * significa "no lo toques" (el request usa `sometimes`), así que vaciar un
 * valor no borraría nada. Son dos intenciones distintas y la pantalla ofrece
 * las dos.
 *
 * `plantas_por_ha` PASA POR `numeroEscrito` COMO LA SUPERFICIE aunque sea un
 * entero, y eso no es de más: el placeholder del campo es "1.111" y ese punto
 * tiene dos lecturas. Sin el parseo chileno, mil ciento once plantas por
 * hectárea se guardarían como una, y la fila quedaría perfectamente formada.
 */
export function camposDesdePlantacion(formulario: FormularioDePlantacion): CamposDeLaFila {
  return {
    especie: formulario.especie.trim(),
    variedad: vacioComoNulo(formulario.variedad),
    anio_plantacion: numeroEscrito(formulario.anio_plantacion),
    superficie_ha: numeroEscrito(formulario.superficie_ha),
    cuartel: vacioComoNulo(formulario.cuartel),
    portainjerto: vacioComoNulo(formulario.portainjerto),
    distancia_plantacion: vacioComoNulo(formulario.distancia_plantacion),
    plantas_por_ha: numeroEscrito(formulario.plantas_por_ha),
    sistema_riego: vacioComoNulo(formulario.sistema_riego),
    nota_terreno: vacioComoNulo(formulario.nota_terreno),
  };
}

function vacioComoNulo(valor: string): string | null {
  const limpio = valor.trim();

  return limpio === "" ? null : limpio;
}

/* --- La agrupación por especie ------------------------------------------- */

/** Una especie del cuadro, con sus filas y su pie. */
export interface GrupoDeEspecie {
  /**
   * La clave normalizada con la que se agrupó. NO se muestra: existe para que
   * React tenga una `key` estable y para que los tests puedan afirmar que dos
   * filas cayeron juntas sin depender de cuál se escribió primero.
   */
  clave: string;
  /**
   * El nombre tal como lo escribió la PRIMERA fila del grupo, que es lo que el
   * cuadro imprime en el encabezado. Se elige la primera y no "la más prolija"
   * porque cualquier otro criterio sería el sistema corrigiéndole la
   * ortografía a quien tasa.
   */
  especie: string;
  filas: PlantacionDeInforme[];
  /** El pie del grupo. Ver {@link totalDeLasFilas}. */
  total: TotalDePlantaciones;
}

export interface TotalDePlantaciones {
  /** En hectáreas. `0` cuando ninguna fila tiene superficie cargada. */
  superficieHa: number;
  /**
   * `null` cuando NINGUNA fila tiene plantas totales, nunca `0`.
   *
   * Un cuadro recién empezado no tiene cero plantas: no tiene el dato. Es la
   * misma regla con la que un valor sin cargar imprime "—".
   */
  plantas: number | null;
}

/**
 * La clave con la que dos filas caen en el mismo grupo.
 *
 * NORMALIZA MÁS QUE EL TRIM: baja a minúsculas, saca las tildes y colapsa los
 * espacios interiores. Así "Cerezos", "cerezos" y "CEREZOS " terminan juntos,
 * y "Limón" con "Limon" también.
 *
 * NINGUNA DE ESAS TRES DIFERENCIAS ES DELIBERADA: nadie escribe una especie en
 * minúscula para decir otra cosa. Lo que la normalización NO hace es unir
 * "Nogal" con "Nogales" ni "Cerezo dulce" con "Cerezo ácido" — esas SÍ podrían
 * ser distinciones queridas, y para el caso en que no lo son está el selector
 * de {@link especiesSugeridas}, que las muestra mientras se escribe.
 *
 * SE AGRUPA POR ESTO Y SE MUESTRA EL TEXTO ORIGINAL: la clave no sale nunca a
 * pantalla, porque "cerezos" en minúscula no es lo que la persona escribió.
 */
export function claveDeEspecie(especie: string): string {
  return especie
    .trim()
    .replace(/\s+/g, " ")
    .toLocaleLowerCase("es-CL")
    // NFD separa la letra de su tilde y el rango U+0300–U+036F —los signos
    // diacríticos combinantes— las borra. Va escrito con escapes y no con los
    // caracteres literales porque un combinante suelto en el código fuente es
    // invisible: se pega a la letra anterior y no se puede leer ni buscar.
    //
    // Es el camino portable para producir una CLAVE: `localeCompare` con
    // sensibilidad de base sabe comparar, pero no devuelve nada con lo que
    // indexar un Map.
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Parte el cuadro en grupos de especie, conservando el orden de carga.
 *
 * EL ORDEN DE LOS GRUPOS ES EL DE SU PRIMERA APARICIÓN, no alfabético: una
 * especie nueva aparece abajo, que es donde la persona acaba de agregarla.
 * Alfabético haría saltar la fila recién guardada a un lugar de la tabla que
 * quien la escribió no está mirando.
 *
 * Y DENTRO DE CADA GRUPO SE CONSERVA EL ORDEN DEL SERVIDOR (por id), que es el
 * de carga. Nada se reordena: lo único que hace esta función es juntar.
 */
export function agruparPorEspecie(filas: PlantacionDeInforme[]): GrupoDeEspecie[] {
  const grupos = new Map<string, { especie: string; filas: PlantacionDeInforme[] }>();

  for (const fila of filas) {
    const clave = claveDeEspecie(fila.especie);
    const grupo = grupos.get(clave);

    if (grupo === undefined) {
      // El nombre del grupo lo fija la PRIMERA fila que lo estrena.
      grupos.set(clave, { especie: fila.especie.trim(), filas: [fila] });
    } else {
      grupo.filas.push(fila);
    }
  }

  return [...grupos].map(([clave, { especie, filas: delGrupo }]) => ({
    clave,
    especie,
    filas: delGrupo,
    total: totalDeLasFilas(delGrupo),
  }));
}

/**
 * El pie de un grupo o del cuadro entero.
 *
 * LA SUPERFICIE SE SUMA EN CENTÉSIMAS, misma regla que `sumarSuperficies` y
 * que el subtotal del cuadro de suelos: en punto flotante `0.1 + 0.2` no da
 * `0.3`, y esta cifra se muestra al lado de las que la componen — un desvío de
 * representación aparecería como una suma que no cuadra con la que quien tasa
 * hizo de cabeza.
 *
 * LAS PLANTAS SE SUMAN YA REDONDEADAS, y de eso depende que el pie coincida
 * con el del prototipo. `plantas_totales` viene redondeada POR FILA desde el
 * servidor: sumando esas cifras, "Cerezos" da 60.536; recalculando desde los
 * productos exactos daría 60.535. Son enteros, así que acá no hay ningún
 * riesgo de punto flotante.
 *
 * LAS FILAS SIN DATO NO SUMAN Y NO ANULAN EL TOTAL: el pie describe lo
 * ESCRITO, igual que el subtotal del cuadro de suelos, y dejarlo en "—" por
 * una fila a medio llenar le quitaría al cuadro su cifra justo cuando ya tiene
 * diez filas buenas. Solo con NINGUNA fila cargada las plantas dan `null`.
 */
export function totalDeLasFilas(filas: PlantacionDeInforme[]): TotalDePlantaciones {
  const centesimas = filas.reduce((total, fila) => {
    const superficie = fila.superficie_ha === null ? null : Number(fila.superficie_ha);

    return superficie === null || !Number.isFinite(superficie)
      ? total
      : total + Math.round(superficie * 100);
  }, 0);

  const conPlantas = filas.filter((fila) => fila.plantas_totales !== null);

  return {
    superficieHa: centesimas / 100,
    plantas:
      conPlantas.length === 0
        ? null
        : conPlantas.reduce((total, fila) => total + (fila.plantas_totales ?? 0), 0),
  };
}

/* --- La ayuda para escribir la especie ------------------------------------ */

/**
 * Las especies ya escritas en ESTE informe que se parecen a lo que se está
 * tecleando.
 *
 * ES TODA LA AYUDA QUE PIDIÓ EL STAKEHOLDER ("una especie de selector mientras
 * se escribe el nombre y si el nombre es similar vaya apareciendo… pero sin
 * una tabla nueva en la bd, es solo UX") y resuelve el defecto concreto que la
 * agrupación tiene: que "Cerezos" y "Cerezo" queden en dos grupos y el cuadro
 * muestre el mismo huerto partido en dos, sin que nada falle.
 *
 * LAS SUGERENCIAS SALEN DE LAS FILAS DEL PROPIO INFORME Y DE NINGÚN CATÁLOGO,
 * y esa es la decisión que importa. Una lista fija de especies chilenas sería
 * un maestro sin dueño: nadie lo mantiene, y sus ausencias se leerían como "esa
 * especie no corresponde". Además el problema a resolver es LOCAL —que dos
 * filas del MISMO predio se escriban igual—, no acordar un vocabulario
 * nacional. La consecuencia aceptada es que la primera fila de un informe no
 * tiene nada que sugerir, y es correcta: todavía no hay ningún grupo al que
 * sumarse.
 *
 * COMPARA NORMALIZADO y por SUBCADENA, no solo por prefijo: quien escribe
 * "cerez" tiene que ver "Cerezos", y quien escribe "dulce" tiene que ver
 * "Cerezo dulce". Las coincidencias por el principio van primero, que es donde
 * el ojo las busca.
 *
 * CON EL CAMPO VACÍO DEVUELVE TODAS, que es lo que hace útil abrir el selector
 * sin haber escrito nada: la lista de especies del informe es corta por
 * naturaleza.
 *
 * LO QUE YA COINCIDE EXACTO NO SE OFRECE: sugerir "Cerezos" a quien acaba de
 * terminar de escribir "Cerezos" es un menú que no propone nada.
 */
export function especiesSugeridas(filas: PlantacionDeInforme[], escrito: string): string[] {
  const buscada = claveDeEspecie(escrito);

  // Primera aparición gana, igual que en los grupos: las dos listas tienen que
  // nombrar cada especie de la misma manera.
  const especies = new Map<string, string>();

  for (const fila of filas) {
    const clave = claveDeEspecie(fila.especie);

    if (clave !== "" && !especies.has(clave)) especies.set(clave, fila.especie.trim());
  }

  if (buscada === "") return [...especies.values()];

  const empiezan: string[] = [];
  const contienen: string[] = [];

  for (const [clave, especie] of especies) {
    if (clave === buscada) continue;

    if (clave.startsWith(buscada)) empiezan.push(especie);
    else if (clave.includes(buscada)) contienen.push(especie);
  }

  return [...empiezan, ...contienen];
}

/**
 * El sustantivo del encabezado, concordado.
 *
 * DEVUELVE SOLO LA FRASE Y NO EL NÚMERO: la cifra va aparte en el componente
 * porque se dibuja en monoespaciada, igual que las de las otras tres pestañas.
 * Devolviéndola acá habría que volver a partirla para poder darle su clase.
 */
export function cuantasPlantaciones(cantidad: number): string {
  return cantidad === 1 ? "plantación registrada" : "plantaciones registradas";
}

/** Cómo se nombra una fila cuando hay que referirse a ella en una frase. */
export function nombreDeLaPlantacion(fila: Pick<PlantacionDeInforme, "especie" | "variedad">): string {
  return fila.variedad?.trim() ? `${fila.especie} ${fila.variedad}` : fila.especie;
}
